import { Gauge, Loader2, MessageSquare } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useMeteringThreadDetail } from '../services/metering.store';

const fmtUsd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(n);

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n);

interface ThreadUsageCardProps {
  threadId: string;
  /** Called when the user wants to read the conversation itself. */
  onOpenConversation: (threadId: string, opts: { branch?: string | null; agentType?: string | null }) => void;
  /** Called to open the full usage breakdown over this page. */
  onOpenUsageDetail: (threadId: string) => void;
}

/**
 * Compact version of the Ellie Usage numbers for a single conversation, for
 * contexts that report on a thread without being the metering dashboard (e.g.
 * an embed session detail). Reads the same endpoint the dashboard does.
 */
export function ThreadUsageCard({ threadId, onOpenConversation, onOpenUsageDetail }: ThreadUsageCardProps) {
  const { data, isLoading, isError } = useMeteringThreadDetail(threadId);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{data?.thread_name || 'Conversation'}</span>
            </CardTitle>
            <p className="font-mono text-xs text-muted-foreground mt-1">{threadId}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => onOpenConversation(threadId, {
                branch: data?.branch,
                agentType: data?.agent_type,
              })}
            >
              View conversation
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
              onClick={() => onOpenUsageDetail(threadId)}
            >
              <Gauge className="h-4 w-4" />
              Usage detail
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading usage…
          </div>
        ) : isError || !data ? (
          <p className="text-sm text-muted-foreground">No usage recorded for this conversation.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Total cost" value={fmtUsd(data.total_cost_usd)} />
            <Stat label="LLM cost" value={fmtUsd(data.llm_cost_usd)} />
            <Stat label="Messages" value={fmtNum(data.message_count)} />
            <Stat label="Tokens" value={fmtNum(data.total_tokens)} />
            <Stat label="Input tokens" value={fmtNum(data.input_tokens)} />
            <Stat label="Output tokens" value={fmtNum(data.output_tokens)} />
            <Stat label="Cache read" value={fmtNum(data.cache_read_input_tokens)} />
            <Stat
              label="Models"
              value={data.models.length ? data.models.join(', ') : '—'}
              mono={false}
            />
          </div>
        )}
        {data?.branch ? (
          <div className="mt-4">
            <Badge variant="outline">{data.branch}</Badge>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, mono = true }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-sm font-medium text-foreground truncate ${mono ? 'tabular-nums' : ''}`} title={value}>
        {value}
      </p>
    </div>
  );
}
